/**
 * Unified Doctor Profile Form Types
 *
 * Consolidates 3 forms into 1 with mode-based field visibility:
 * - EditUserProfile (self-edit for both doctor types)
 * - UpdateDoctorSubstitute (admin editing substitute)
 * - AddOfficeDoctorAttending (admin editing attending)
 */

/**
 * Form operation modes determining which fields are visible/required
 */
export type DoctorProfileFormMode =
  | 'substitute-profile' // Self-edit by substitute doctor
  | 'substitute-admin' // Admin editing substitute doctor
  | 'attending-profile' // Self-edit by attending doctor
  | 'attending-admin'; // Admin editing attending doctor

/**
 * Field groups that can be shown/hidden based on mode
 */
export type FieldGroup =
  | 'avatar'
  | 'identity' // firstname, lastname, phoneNumber
  | 'dateOfBirth'
  | 'address'
  | 'orderRegistrationNumber'
  | 'rpps'
  | 'thesis'
  | 'rhythm'
  | 'banking' // iban, bic
  | 'signature'
  | 'commission'
  | 'doctolibId'
  | 'installable'
  | 'priority' // Admin-only ranking of a substitute (admin/support/csm)
  | 'contractProfile'; // URSSAF + licence de remplacement (issue #796)

/**
 * Field visibility matrix per mode
 *
 * | Field        | substitute-profile | substitute-admin | attending-profile | attending-admin |
 * |--------------|-------------------|------------------|-------------------|-----------------|
 * | Avatar       | ✓                 | ✓                | ✓                 | ✗               |
 * | Identity     | ✓                 | ✓                | ✓                 | ✓               |
 * | DOB          | ✓                 | ✓                | ✓                 | ✗               |
 * | Address      | ✓                 | ✓                | ✓                 | ✗               |
 * | Order Reg #  | ✓                 | ✓                | ✓                 | ✓               |
 * | RPPS         | ✓                 | ✓                | ✓                 | ✗               |
 * | Thesis       | ✓                 | ✓                | ✓                 | ✗               |
 * | Rhythm       | ✓                 | ✓                | ✓                 | ✓               |
 * | Banking      | ✓                 | ✓                | ✓                 | ✓               |
 * | Signature    | ✓                 | ✗                | ✓                 | ✓               |
 * | Commission   | ✗                 | ✗                | ✗                 | ✓               |
 * | Doctolib ID  | ✗                 | ✓                | ✗                 | ✓               |
 * | Priority     | ✗                 | ✓                | ✗                 | ✗               |
 * | Contract     | ✓                 | ✓                | ✗                 | ✗               |
 */
export const FIELD_VISIBILITY: Record<
  DoctorProfileFormMode,
  Set<FieldGroup>
> = {
  'substitute-profile': new Set([
    'avatar',
    'identity',
    'dateOfBirth',
    'address',
    'orderRegistrationNumber',
    'rpps',
    'thesis',
    'rhythm',
    'banking',
    'signature',
    'contractProfile',
  ]),
  'substitute-admin': new Set([
    'avatar',
    'identity',
    'dateOfBirth',
    'address',
    'orderRegistrationNumber',
    'rpps',
    'thesis',
    'installable',
    'rhythm',
    'banking',
    'doctolibId',
    'priority',
    'contractProfile',
    // No signature for admin editing substitute
  ]),
  'attending-profile': new Set([
    'avatar',
    'identity',
    'dateOfBirth',
    'address',
    'orderRegistrationNumber',
    'rpps',
    'thesis',
    'rhythm',
    'banking',
    'signature',
  ]),
  'attending-admin': new Set([
    // No avatar, DOB, address, RPPS, thesis for admin editing attending
    'identity',
    'orderRegistrationNumber',
    'rhythm',
    'banking',
    'signature',
    'commission',
    'doctolibId',
  ]),
};

/**
 * Helper to check if a field group is visible for a given mode
 */
export function isFieldVisible(
  mode: DoctorProfileFormMode,
  field: FieldGroup,
): boolean {
  return FIELD_VISIBILITY[mode].has(field);
}

/**
 * Props for field visibility - can be passed to sub-components
 */
export interface FieldVisibilityProps {
  mode: DoctorProfileFormMode;
}
